<script setup lang="ts">
import { onMounted, watch, ref } from "vue";
import { useRoute, useRouter } from "vue-router";
import PersonalSelectionPanel from './components/nutrition/PersonalSelectionPanel.vue';
import PersonalSelectionConfirmDialog from './components/nutrition/PersonalSelectionConfirmDialog.vue';
import { useNutritionSelectionStore } from './stores/nutrition-selection';
import { useRecipePurchaseStore } from './stores/recipe-purchase';
import { resetRecipeBookForBuyer } from './cdp/recipe-book-cache';
import AppHeader from "./components/AppHeader.vue";
import BottomNav from "./components/BottomNav.vue";
import LoyaltyTabBar from "./components/LoyaltyTabBar.vue";
import ExperienceOverlay from "./components/ExperienceOverlay.vue";
import AssistantLaunch from "./components/AssistantLaunch.vue";
import { useAuthStore } from "./stores/auth";
import { useCartStore } from "./stores/cart";
import { useOutletStore } from "./stores/outlet";
import { useChatStore } from "./stores/chat";
import { useCdpStore } from "./stores/cdp";
import { useCdpTracking } from "./composables/useCdpTracking";
import { isDarkTheme } from "./theme";

const route = useRoute();
const router = useRouter();
const auth = useAuthStore();
const cart = useCartStore();
const outlet = useOutletStore();
const chat = useChatStore();
const cdp = useCdpStore();
const personalSelection = useNutritionSelectionStore();
const personalSelectionEnabled = ref(false);
watch(() => [auth.profileId, auth.buyerGeneration], () => {
  personalSelection.resetForBuyer();
  useRecipePurchaseStore().resetForBuyer();
  resetRecipeBookForBuyer();
  cdp.reset();
  void cdp.init();
  if (chat.initialized) void chat.reload();
});
watch(isDarkTheme, (dark) => {
  chat.setTheme(dark ? "dark" : "light");
});

// Фаза 2 — браузерный веб-SDK CDP (события по write-key) + доставка опытов на
// витрину (оверлей ниже). Само-выключается, если у BFF нет write-key.
useCdpTracking(router);

onMounted(async () => {
  // Точки доставки — публичны (видны и анониму): грузим на холодном старте, чтобы шапка сразу
  // показала выбранную/ближайшую точку. Не блокирует и не роняет загрузку витрины.
  void outlet.load();
  // Try restore session via cookie on cold load.
  await auth.fetchMe().catch(() => undefined);
  // Корзину грузим и ГОСТЮ: она живёт на анонимном профиле (сервер), поэтому счётчик в
  // навбаре отражает сохранённую корзину гостя сразу на холодном старте — а не только
  // после первого клика или открытия страницы корзины. Best-effort, витрину не роняет.
  await cart.fetch().catch(() => undefined);
  // «Помощник покупок» (ТЗ §7.6) — плавающая кнопка-лаунчер поверх всей витрины.
  // Монтируется один раз на всё приложение (не на каждую страницу); молча
  // выключается, если инструмент-чат не настроен на этом деплое.
  void chat.init();
  try {
    const configResponse = await fetch('/api/v1/personal-selection/config', {credentials:'include'});
    personalSelectionEnabled.value = configResponse.ok && (await configResponse.json()).enabled === true;
    if (personalSelectionEnabled.value) await personalSelection.load();
  } catch { personalSelectionEnabled.value = false; }
});
</script>

<template>
  <div class="min-h-screen bg-vd-page flex flex-col">
    <!-- Магазинная шапка/меню — только в магазине. Портал (layout=portal) и голые
         экраны (bare) их не показывают; у Портала своё лоялти-меню внизу. -->
    <AppHeader v-if="route.meta.layout !== 'bare' && route.meta.layout !== 'portal'" />

    <main class="flex-1 w-full pb-[calc(var(--vd-nav-h,72px)+env(safe-area-inset-bottom)+16px)]">
      <div v-if="personalSelectionEnabled && route.meta.layout !== 'bare' && route.meta.layout !== 'portal'" class="vd-container pt-3">
        <PersonalSelectionPanel />
      </div>
      <PersonalSelectionConfirmDialog />
      <nav v-if="route.meta.layout !== 'bare' && route.meta.layout !== 'portal'" class="vd-container pt-3 flex gap-3 text-sm" aria-label="Подбор еды">
        <RouterLink to="/products/nutrition" class="vd-link">Продукты по КБЖУ</RouterLink><RouterLink to="/recipes" class="vd-link">Рецепты</RouterLink><RouterLink to="/menu" class="vd-link">Меню</RouterLink>
      </nav>
      <RouterView :key="`${auth.profileId ?? 'anonymous'}:${auth.buyerGeneration}:${route.path}`" />
      <footer v-if="route.meta.layout !== 'bare' && route.meta.layout !== 'portal'" class="vd-container mt-8 py-5 lg:pr-80 text-xs leading-relaxed text-vd-muted">
        Демонстрационная витрина. Каталог содержит демонстрационные товары и изображения, созданные с помощью ИИ.
        Характеристики приведены по справочным данным и прототипам товаров.
      </footer>
    </main>

    <BottomNav v-if="route.meta.layout !== 'bare' && route.meta.layout !== 'portal'" />
    <LoyaltyTabBar v-if="route.meta.layout === 'portal'" />
    <!-- CDP experiences (Фаза 2): попапы/офферы/опросы по write-key. Работает и для
         гостя (у гостя тоже есть CDP-профиль); само-выключается без write-key. -->
    <ExperienceOverlay />
    <div
      v-if="route.meta.layout !== 'bare'"
      class="vd-assistant-entry fixed bottom-24 right-3 z-40 max-w-[240px] md:bottom-6 md:right-6"
      :class="{ 'hidden md:block': route.meta.layout !== 'portal' }"
    >
      <AssistantLaunch floating />
    </div>
  </div>
</template>

<style>
:root {
  --vd-bottom-pad: 72px;
}
@media (min-width: 768px) {
  :root {
    --vd-bottom-pad: 24px;
  }
}
/* Графический вход располагается над строкой покупки, сохраняя доступ к её кнопкам. */
body:has([data-sticky-cta]) .vd-assistant-entry {
  bottom: calc(var(--vd-nav-h, 0px) + 76px + env(safe-area-inset-bottom));
}
</style>
